import React from 'react';

/**
 * REVA brand mark — "Operator Nexus".
 *
 * Hybrid concept: Neural lattice × Operator glyph.
 *   • A custom angular R-form, drawn as a connected graph of nodes + edges
 *   • Three core nodes (data → reasoning → decision) wired by hairline edges
 *   • Two secondary nodes (predictive / signal) branching from the R's leg
 *   • A single luminous AI pulse node at the apex of the R
 *
 * What it says without being literal:
 *   • "R" → REVA, recognisably the brand
 *   • node graph → AI / data-power language (Palantir, Vercel ai-sdk, NVEIL)
 *   • asymmetric edges → infrastructure topology, not a logo template
 *   • bright apex node → predictive signal, the AI command beacon
 *
 * Variants: "full" (mark + wordmark) | "icon" | "wordmark"
 * Themes:   "light" — for white surfaces      → ink mark, cyan signal
 *           "dark"  — for obsidian surfaces   → white mark, cyan signal
 *           "mono"  — single colour (print)
 */

const SIGNAL_EMERALD = '#00A86B';
const SIGNAL_EMERALD_BRIGHT = '#1FD18A';

export default function RevaLogo({
  variant = 'full',
  size,
  theme = 'light',
  className = '',
  glow = false,
}) {
  const isDark = theme === 'dark';
  const isMono = theme === 'mono';

  const inkColor    = isDark ? '#F2F4F6' : (isMono ? '#0E1A14' : '#0E1320');
  const edgeColor   = isDark ? 'rgba(242,244,246,0.50)' : (isMono ? 'rgba(14,26,20,0.4)' : 'rgba(14,19,32,0.42)');
  const accentColor = isMono ? inkColor : SIGNAL_EMERALD;
  const accentBright = isMono ? inkColor : SIGNAL_EMERALD_BRIGHT;
  const wordColor   = isDark ? '#F2F4F6' : '#0E1320';
  const haloColor   = isDark ? '#0A0D10' : '#FFFFFF';

  const uid = React.useId();
  const filterId = `reva-pulse-${uid}`;

  // Node coordinates inside the 64×64 viewBox — form an angular R-glyph as a graph
  // Apex (pulse), trunk-top, junction, head-right, leg-mid, leg-foot.
  const N = {
    apex:      [44, 8],   // luminous AI beacon
    trunkTop:  [16, 12],
    head:      [40, 18],
    junction:  [16, 32],  // R's elbow
    arm:       [38, 32],  // R's inner arm
    legMid:    [28, 42],  // R's diagonal leg
    legFoot:   [44, 56],
    trunkFoot: [16, 56],
    sensor:    [54, 38],  // outer predictive node (orbit hint)
  };
  // Edges (graph topology) → conceptually wire up the R
  const E = [
    ['trunkTop', 'head'],      // R top crossbar
    ['head', 'arm'],           // R right side
    ['trunkTop', 'junction'],  // R trunk top half
    ['junction', 'arm'],       // R inner crossbar
    ['junction', 'trunkFoot'], // R trunk bottom half
    ['junction', 'legMid'],    // R diagonal leg start
    ['legMid', 'legFoot'],     // R diagonal leg end
    ['arm', 'legMid'],         // inner reinforcement edge
    ['head', 'sensor'],        // branch toward predictive sensor
    ['sensor', 'legFoot'],     // closing loop
    ['trunkTop', 'apex'],      // wire to the apex pulse
  ];

  const Mark = ({ s = 32 }) => (
    <svg
      width={s}
      height={s}
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="flex-shrink-0"
      role="img"
      aria-label="REVA"
    >
      {glow && (
        <defs>
          <filter id={filterId} x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="2.4" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
      )}

      {/* ── Edges (hairline graph wiring) ───────────────────────────── */}
      <g stroke={edgeColor} strokeWidth="1.4" strokeLinecap="round">
        {E.map(([a, b], i) => {
          const [x1, y1] = N[a];
          const [x2, y2] = N[b];
          // The apex edge gets the signal accent
          const isSignal = a === 'apex' || b === 'apex';
          return (
            <line
              key={i}
              x1={x1} y1={y1} x2={x2} y2={y2}
              stroke={isSignal ? accentColor : edgeColor}
              opacity={isSignal ? 0.85 : 1}
            />
          );
        })}
      </g>

      {/* ── Primary nodes (R-glyph anchors) — solid ink ────────────── */}
      <g fill={inkColor}>
        {['trunkTop', 'head', 'junction', 'arm', 'legMid', 'legFoot', 'trunkFoot'].map((k) => {
          const [x, y] = N[k];
          return <circle key={k} cx={x} cy={y} r="2.6" />;
        })}
      </g>

      {/* ── Secondary node (orbital sensor) — outline only ─────────── */}
      <circle
        cx={N.sensor[0]} cy={N.sensor[1]} r="2.4"
        fill={haloColor}
        stroke={inkColor}
        strokeWidth="1.4"
      />

      {/* ── Apex pulse: the AI beacon ───────────────────────────────── */}
      <g filter={glow ? `url(#${filterId})` : undefined}>
        {/* outer halo for separation on any bg */}
        <circle cx={N.apex[0]} cy={N.apex[1]} r="5.6" fill={haloColor} />
        {/* emerald gradient pulse */}
        <circle cx={N.apex[0]} cy={N.apex[1]} r="4.2" fill={accentColor} />
        <circle cx={N.apex[0]} cy={N.apex[1]} r="3.0" fill={accentBright} opacity="0.9" />
        {/* inner crisp dot — keeps the beacon legible at favicon scale */}
        <circle cx={N.apex[0]} cy={N.apex[1]} r="1.2" fill={haloColor} opacity="0.95" />
      </g>
    </svg>
  );

  if (variant === 'icon') {
    return <Mark s={size || 32} />;
  }

  if (variant === 'wordmark') {
    return (
      <span
        className={`font-heading font-extrabold ${className}`}
        style={{
          color: wordColor,
          fontSize: size || 22,
          letterSpacing: '0.14em',
          lineHeight: 1,
        }}
      >
        REVA
      </span>
    );
  }

  // Full — mark + wordmark
  const iconSize = size || 32;
  const fontSize = Math.round(iconSize * 0.62);

  return (
    <div className={`inline-flex items-center gap-2.5 ${className}`}>
      <Mark s={iconSize} />
      <span
        className="font-heading font-extrabold"
        style={{
          color: wordColor,
          fontSize,
          letterSpacing: '0.14em',
          lineHeight: 1,
        }}
      >
        REVA
      </span>
    </div>
  );
}
